import React from "react";
import { observer } from "mobx-react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import styled from "styled-components";
import { colors } from "../theme";
import { waitlistController } from "../modules/controllers";

// ---------------------------------------------------------------------------
// Styled components — mirrors the Login page aesthetic
// ---------------------------------------------------------------------------

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${colors.primaryBackground};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 20px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        ellipse at 20% 10%,
        rgba(204, 78, 48, 0.1) 0%,
        transparent 55%
      ),
      radial-gradient(
        ellipse at 80% 90%,
        rgba(204, 78, 48, 0.06) 0%,
        transparent 55%
      );
    pointer-events: none;
  }
`;

const Card = styled(Paper)`
  max-width: 520px;
  width: 100%;
  padding: 52px 48px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.97) !important;
  box-shadow:
    0 8px 32px rgba(44, 24, 16, 0.1),
    0 2px 8px rgba(204, 78, 48, 0.08) !important;
  position: relative;
  z-index: 1;
  text-align: center;

  @media (max-width: 600px) {
    padding: 36px 24px;
    margin: 0 4px;
  }
`;

const LogoImage = styled.img`
  height: 180px;
  width: auto;
  margin-bottom: 20px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.12));
`;

const Headline = styled(Typography)`
  font-size: 1.5rem !important;
  font-weight: 700 !important;
  color: ${colors.primary} !important;
  font-family: "Montserrat", sans-serif !important;
  letter-spacing: -0.02em !important;
  line-height: 1.2 !important;
  margin-bottom: 12px !important;

  @media (max-width: 600px) {
    font-size: 1.25rem !important;
  }
`;

const Subheadline = styled(Typography)`
  color: ${colors.text.secondary} !important;
  font-family: "Montserrat", sans-serif !important;
  font-weight: 400 !important;
  line-height: 1.6 !important;
  font-size: 0.975rem !important;
  margin-bottom: 36px !important;
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 12px;
    font-family: "Montserrat", sans-serif;

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.primary};
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.primary};
    }
  }

  .MuiInputLabel-root.Mui-focused {
    color: ${colors.primary};
  }

  .MuiInputLabel-root {
    font-family: "Montserrat", sans-serif;
  }
`;

const ConsentLabel = styled(FormControlLabel)`
  margin: 4px 0 !important;
  align-items: flex-start !important;

  .MuiCheckbox-root {
    padding-top: 2px;
    color: ${colors.text.secondary};

    &.Mui-checked {
      color: ${colors.primary};
    }
  }

  .MuiFormControlLabel-label {
    font-family: "Montserrat", sans-serif;
    font-size: 0.825rem;
    color: ${colors.text.secondary};
    line-height: 1.5;
    text-align: left;
  }
`;

const SubmitButton = styled(Button)`
  padding: 16px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  border-radius: 14px !important;
  background: ${colors.gradients.primary} !important;
  text-transform: none !important;
  font-family: "Montserrat", sans-serif !important;
  box-shadow: 0 6px 20px rgba(204, 78, 48, 0.3) !important;
  transition: all 0.3s ease !important;
  margin-top: 8px !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(204, 78, 48, 0.4) !important;
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc !important;
    box-shadow: none !important;
    transform: none !important;
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Waitlist = observer(() => {
  const ctrl = waitlistController;

  const handleSubmit = (e) => {
    e.preventDefault();
    ctrl.submit();
  };

  if (ctrl.isSuccess) {
    return (
      <PageContainer>
        <Card elevation={0}>
          <LogoImage src="/logo_vertical.png" alt="Culinary IQ" />
          <SuccessContainer>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 64, color: colors.status.success }}
            />
            <Typography
              variant="h5"
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 700,
                color: colors.text.primary,
              }}
            >
              You're on the list!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                color: colors.text.secondary,
                lineHeight: 1.6,
              }}
            >
              {ctrl.successMessage}
            </Typography>
          </SuccessContainer>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card elevation={0}>
        <LogoImage src="/logo_vertical.png" alt="Culinary IQ" />

        <Headline variant="h2">Coming Soon</Headline>
        <Subheadline variant="body1">
          Join the waitlist to be the first to know!
        </Subheadline>

        {ctrl.errorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2, textAlign: "left" }}
          >
            {ctrl.errorMessage}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <StyledTextField
            label="Full Name"
            value={ctrl.fullName}
            onChange={(e) => ctrl.setFullName(e.target.value)}
            variant="outlined"
            fullWidth
            autoComplete="name"
          />
          <StyledTextField
            label="Email Address"
            type="email"
            value={ctrl.email}
            onChange={(e) => ctrl.setEmail(e.target.value)}
            variant="outlined"
            fullWidth
            required
            autoComplete="email"
          />

          <ConsentLabel
            control={
              <Checkbox
                checked={ctrl.consent}
                onChange={(e) => ctrl.setConsent(e.target.checked)}
                size="small"
              />
            }
            label="I agree to receive product updates and launch announcements from Culinary IQ."
          />

          <SubmitButton
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={!ctrl.canSubmit}
          >
            {ctrl.isSubmitting ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Join the Waitlist"
            )}
          </SubmitButton>
        </Box>
      </Card>
    </PageContainer>
  );
});

export default Waitlist;
